import React, { useCallback, useEffect } from 'react';
import { DataGridPro } from '@mui/x-data-grid-pro';
import DisplayHelper from 'webapp/helpers/display_helper';
import { Button, Checkbox, IconButton, Select, Stack, Tooltip } from '@mui/material';
import Actions from 'webapp/actions/actions';
import { stripNonAlpha } from 'webapp/helpers/string_helper';
import { Delete } from '@mui/icons-material';
import { useSelector } from 'react-redux';

const compare = new Intl.Collator('en').compare;

const dollarFormatter = (value) => (value == null ? '$0' : DisplayHelper.formatMoneyComma(value));

const getRowPayment = (row) => row.monthlyPaymentOverride ?? row.scheduledMonthlyPayment ?? 0;

const getRowIdentifier = (row) => `${stripNonAlpha(row.subject)}-${stripNonAlpha(row.accountNumber)}`;

const defaultCol = {
  sortable: false,
  flex: 10,
};

const dollarCol = {
  ...defaultCol,
  type: 'number',
  valueFormatter: ({ value }) => (value === 0 ? '$0' : dollarFormatter(value)),
};

const dateCol = {
  ...defaultCol,
  flex: 8,
  sortComparator: (v1, v2) => compare(v1 === null ? '9999-99-99' : v1, v2 === null ? '9999-99-99' : v2),
};

export default function Tradelines(props) {
  let { locked, rows, setRows, monthlyDebt, setMonthlyDebt, mini } = props;
  const decisionPageState = useSelector((redux) => redux.decisioningPageData);
  const hasVOMStip = decisionPageState?.stipulations?.some(
    (s) => s.type === 'VOM' && ((s.files?.length !== 0 && s.status !== 'Approved') || s.status === 'Pending')
  );

  const updateRow = useCallback(
    (id, newRow) => {
      if (!id && id !== 0) {
        const newId = Math.max(...rows.map((r) => r.id)) + 1;
        setRows([...rows, { ...newRow, id: newId, errors: false }]);
      } else {
        setRows(rows?.map((r) => (r.id === id ? { ...r, ...newRow, errors: false } : { ...r, errors: false })));
      }
    },
    [rows, setRows]
  );

  useEffect(() => {
    setMonthlyDebt(rows.reduce((sum, cur) => sum + (cur.includeInDTI ? getRowPayment(cur) : 0), 0));
  }, [setMonthlyDebt, rows]);

  const monthlyAmountDisplay = (row) => {
    if (row.isNew && !row.scheduledMonthlyPayment) {
      return <span style={{ color: 'red' }}>Monthly Payment</span>;
    }

    return row.monthlyPaymentOverride != null ? (
      <Tooltip title={row.monthlyPaymentOverride ? `Original: ${dollarFormatter(row.scheduledMonthlyPayment)}` : ''}>
        <span style={{ width: '100%', textAlign: 'end', backgroundColor: 'LemonChiffon' }}>
          {dollarFormatter(row.monthlyPaymentOverride)}
        </span>
      </Tooltip>
    ) : (
      <span>{dollarFormatter(row.scheduledMonthlyPayment)}</span>
    );
  };

  const columns = [
    { field: 'id', hide: true },
    {
      field: 'subject',
      headerName: 'Subject',
      ...defaultCol,
      flex: 8,
      hide: mini,
      editable: !locked,
      renderCell: ({ row }) =>
        !row.isNew ? (
          row.subject
        ) : (
          <Select
            label="applicant"
            native={true}
            style={{ padding: '0', width: '100%', fontSize: '12.25px' }}
            size="small"
            margin="dense"
            variant="standard"
            onChange={(e) => updateRow(row.id, { subject: e.target.value })}
          >
            <option value="Applicant">Applicant</option>
            <option value="Co-Applicant">Co-Applicant</option>
          </Select>
        ),
    },
    {
      field: 'memberName',
      headerName: 'Entity Name',
      ...defaultCol,
      editable: !locked,
      cellClassName: ({ row }) => `qa-entity-${getRowIdentifier(row)}`,
      renderCell: ({ row }) =>
        row.isNew && !row.memberName ? <span style={{ color: 'red' }}>Entity Name</span> : row.memberName,
    },
    { field: 'accountNumber', headerName: 'Account #', ...defaultCol, hide: mini },
    { field: 'isNew', headerName: 'isNew', hide: true },
    { field: 'portfolioType', headerName: 'Account Type', ...defaultCol },
    { field: 'dateOpened', headerName: 'Date Opened', ...dateCol, hide: mini },
    { field: 'dateClosed', headerName: 'Date Closed', ...dateCol, hide: mini },
    {
      field: 'currentBalance',
      headerName: 'Balance',
      ...dollarCol,
      flex: 8,
      editable: !locked,
      cellClassName: ({ row }) => `qa-balance-${getRowIdentifier(row)}`,
      renderCell: ({ row }) =>
        row.isNew && !row.currentBalance ? (
          <span style={{ color: 'red' }}>Balance</span>
        ) : (
          dollarFormatter(row.currentBalance)
        ),
    },
    { field: 'highCredit', headerName: 'High Balance', ...dollarCol, flex: 8, hide: mini },
    {
      field: 'scheduledMonthlyPayment',
      headerName: 'Monthly Payment',
      ...dollarCol,
      editable: !locked,
      valueGetter: ({ row }) => getRowPayment(row),
      cellClassName: ({ row }) => `qa-monthly-payment-${getRowIdentifier(row)}`,
      renderCell: ({ row }) => monthlyAmountDisplay(row),
    },
    {
      field: 'paymentScheduleMonthCount',
      headerName: 'Term (Mos)',
      ...defaultCol,
      flex: 5,
      type: 'number',
      hide: mini,
    },
    {
      field: 'includeInDTI',
      headerName: 'Include?',
      ...defaultCol,
      flex: 4,
      editable: !locked,
      renderCell: ({ row }) => (
        <Checkbox
          className={`qa-include-${getRowIdentifier(row)}`}
          color="primary"
          checked={row.includeInDTI}
          disabled={locked || !!row.dateClosed || !row.currentBalance}
          onChange={(e) => {
            updateRow(row.id, { ...row, includeInDTI: e.target.checked });
          }}
        />
      ),
      hide: mini,
    },
    {
      field: '',
      headerName: 'Actions',
      ...defaultCol,
      flex: 4,
      renderCell: ({ row }) => (
        <>
          {row?.isNew && (
            <IconButton
              className="uw-remove-tradeline"
              aria-label="remove-tradeline"
              title="Remove Tradeline"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setRows(rows.filter((r) => r.id !== row.id));
              }}
            >
              <Delete />
            </IconButton>
          )}
        </>
      ),
      hide: mini,
    },
  ].filter((e) => e);

  return (
    <>
      {!mini && (
        <div id="total-debt-header" className="total-debt-header">
          Total Monthly Debt Payments: {dollarFormatter(monthlyDebt)}
        </div>
      )}
      <hr />
      <div id="tradelines-table" style={{ height: mini ? 240 : 410, width: '100%' }}>
        <DataGridPro
          disableVirtualization={true}
          className="tradelines-datagrid"
          columns={columns}
          rows={rows}
          rowHeight={30}
          disableColumnMenu={true}
          hideFooterRowCount={mini}
          hideFooter={mini}
          components={{
            NoRowsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center" id="qa-tradeline-rows-empty">
                No rows
              </Stack>
            ),
            Footer: () => (
              <div className="footerContainer">
                {!locked && hasVOMStip && (
                  <Button
                    variant="contained"
                    className="add-tradeline-btn"
                    onClick={() => {
                      updateRow(null, {
                        portfolioType: 'mortgage',
                        isNew: true,
                        subject: 'Applicant',
                        includeInDTI: true,
                      });
                    }}
                  >
                    Add Tradeline
                  </Button>
                )}
                <div style={{ marginLeft: 'auto' }}>
                  <div className="row-count">{`Total Rows: ${rows?.length}`}</div>
                </div>
              </div>
            ),
          }}
          noResultsOverlay={'empty data here'}
          getRowClassName={({ row }) => (row.errors ? 'uw-tradeline-error' : '')}
          sortModel={[
            { field: 'isNew', sort: 'asc' },
            { field: 'subject', sort: 'asc' },
            { field: 'dateClosed', sort: 'desc' },
            { field: 'dateOpened', sort: 'desc' },
            { field: 'memberName', sort: 'asc' },
            { field: 'accountNumber', sort: 'asc' },
            { field: 'id', sort: 'asc' },
          ]}
          isCellEditable={(params) =>
            (['currentBalance', 'subject', 'memberName', 'includeInDTI'].includes(params.field) && params.row.isNew) ||
            params.field === 'scheduledMonthlyPayment'
          }
          onCellEditCommit={useCallback(
            ({ id, field, value }) => {
              if (field === 'scheduledMonthlyPayment') {
                if (value <= 0) {
                  Actions.setErrorMessages(['Payment amount must be greater than zero.']);
                } else if (rows[id].isNew) {
                  updateRow(id, { ...rows[id], [field]: value });
                } else if (value === rows[id].scheduledMonthlyPayment || value == null) {
                  updateRow(id, { ...rows[id], monthlyPaymentOverride: null });
                } else {
                  updateRow(id, { ...rows[id], monthlyPaymentOverride: value });
                }
              } else {
                updateRow(id, { ...rows[id], [field]: value });
              }
            },
            [rows, updateRow]
          )}
        />
      </div>
    </>
  );
}
